import React, { useState } from 'react';

import classNames from 'classnames';
import ProgressIcon from 'icons/progress.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import Checkbox from 'components/application/Checkbox';
import ModalBox from 'components/application/ModalBox';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';

import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function UnassignPersonnelModal({
  domainProjectPersonnel,
  isSubmitting,
  onClose,
  onSubmitClick,
  projectPersonnelIds,
}) {
  const [selectedProjectPersonnelIds, setSelectedProjectPersonnelIds] =
    useState(projectPersonnelIds);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectPersonnel.components.UnassignPersonnelModal',
  });

  const { t: i18nTShared } = useTranslation('common', { keyPrefix: 'shared' });

  const handleProjectPersonnelToggle = (projectPersonnelId) => {
    setSelectedProjectPersonnelIds((prevState) => {
      const newSelectedProjectPersonnelIds = new Set(prevState);

      if (prevState.has(projectPersonnelId)) {
        newSelectedProjectPersonnelIds.delete(projectPersonnelId);
      } else {
        newSelectedProjectPersonnelIds.add(projectPersonnelId);
      }

      return newSelectedProjectPersonnelIds;
    });
  };

  return (
    <ModalBox
      customFooter={
        <div className='tw-ml-auto tw-flex tw-items-center tw-gap-3'>
          <TextButton
            color='blue'
            disabled={isSubmitting}
            onClick={onClose}
            size='md'
          >
            {i18nTShared('cancel')}
          </TextButton>
          <FilledButton
            color='red'
            disabled={isSubmitting || selectedProjectPersonnelIds.size === 0}
            onClick={() => onSubmitClick(selectedProjectPersonnelIds)}
          >
            {isSubmitting ?
              <>
                <ProgressIcon
                  className='tw-mr-1 [&_path]:tw-fill-white'
                  height={18}
                  width={18}
                />
                {i18nTShared('pleaseWait...')}
              </>
            : i18nT('button', { count: selectedProjectPersonnelIds.size })}
          </FilledButton>
        </div>
      }
      isOpen={true}
      modalBoxClassName='tw-max-w-[560px]'
      onClose={onClose}
    >
      <div className='tw-flex tw-h-112 tw-flex-col tw-gap-6 tw-p-6 tw-pb-0'>
        <div className='tw-flex tw-flex-col tw-gap-2'>
          <p className='tw-mb-0 tw-font-inter tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
            {i18nT('title')}
          </p>
          <p className='tw-mb-0 tw-font-inter tw-text-m tw-font-normal tw-tracking-auto tw-text-grey-600'>
            {i18nT('description', { count: selectedProjectPersonnelIds.size })}
          </p>
        </div>
        <div className='tw-relative tw-h-full tw-overflow-y-auto'>
          {domainProjectPersonnel.data
            .filter((projectPersonnel) =>
              projectPersonnelIds.has(projectPersonnel.id),
            )
            .map((projectPersonnel, index) => {
              const {
                id: projectPersonnelId,
                relationships: {
                  companyRole: { data: companyRoleData },
                  personnel: {
                    data: { id: personnelId },
                  },
                },
              } = projectPersonnel;

              const {
                attributes: { firstName, lastName },
                relationships: {
                  profilePhoto: { data: profilePhotoData },
                },
              } = domainProjectPersonnel.included.personnel[personnelId];

              const companyRole =
                companyRoleData &&
                domainProjectPersonnel.included.companyRole[companyRoleData.id];

              const profilePhoto =
                profilePhotoData &&
                domainProjectPersonnel.included.profilePhoto[
                  profilePhotoData.id
                ];

              const isSelected =
                selectedProjectPersonnelIds.has(projectPersonnelId);

              const name = personDisplayName({
                attributes: { firstName, lastName },
              });

              return (
                <div
                  className={classNames(
                    'tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-solid tw-border-grey-100 tw-py-4',
                    index !== 0 && 'tw-border-t-1',
                  )}
                  key={projectPersonnelId}
                >
                  <div className='tw-flex tw-h-full tw-items-center tw-px-2'>
                    <Checkbox
                      isChecked={isSelected}
                      isDisabled={isSubmitting}
                      label={i18nTShared(
                        isSelected ? 'deselectName' : 'selectName',
                        { interpolation: { escapeValue: false }, name },
                      )}
                      name='select-project-personnel'
                      onChange={() => {
                        handleProjectPersonnelToggle(projectPersonnelId);
                      }}
                    />
                  </div>
                  <ProfilePhotoDisplay
                    componentSize='sm'
                    photoUrl={profilePhoto?.links?.croppedUrl}
                    profileInitials={`${firstName[0]}${lastName[0]}`}
                  />
                  <div
                    className={classNames(
                      'tw-flex tw-h-10 tw-flex-col',
                      companyRole ? 'tw-justify-between' : 'tw-justify-center',
                    )}
                  >
                    <p className='tw-mb-0 tw-truncate tw-text-m tw-font-medium tw-text-grey-900'>
                      {name}
                    </p>
                    {companyRole && (
                      <p className='tw-mb-0 tw-truncate tw-text-s tw-font-normal tw-text-grey-500'>
                        {companyRole.attributes.position}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </ModalBox>
  );
}

UnassignPersonnelModal.propTypes = {
  domainProjectPersonnel: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('projectPersonnel')).isRequired,
    included: PropTypes.shape({
      companyRole: PropTypes.objectOf(resourceShape('companyRole')).isRequired,
      personnel: PropTypes.objectOf(resourceShape('personnel')).isRequired,
      profilePhoto: PropTypes.objectOf(resourceShape('profilePhoto'))
        .isRequired,
    }).isRequired,
  }).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitClick: PropTypes.func.isRequired,
  projectPersonnelIds: PropTypes.instanceOf(Set).isRequired,
};
